/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { GetDeliveryOrderByIdObject } from "../../../@types/DRO/DeliveryOrderResponse";
import { VendorListArray } from "../../../@types/DRO/GetAllVendorsResponse";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import {
  CreateDeliveryOrderBody,
  CreateDeliveryOrderRecievedBody,
} from "../../../@types/DTO/CreateDeliveryOrderBody";
import { ReturnOrderRequestBody } from "../../../@types/DTO/ReturnOrderBody";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import { DeliveryReturnApi } from "../../../api/DeliveryReturn/DeliveryReturnApi";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { UIDPartApi } from "../../../api/UID-Part/UIDPartApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import { DeliveryOrderHistory } from "../OrderHistory/DeliveryOrderHistory";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface CreateDeliveryOrderDraftProps {
  onBack: () => void;
  orderDataId?: number | null;
  orderStage: string;
}

interface DeliveryOrderItem extends TransferOrderPartDetails {
  partId: number;
  received: number;
  billed: number;
  unit: string;
  total: number;
  hsn?: string;
  isNew?: boolean;
  quantityPendingToReturn?: number;
}

// Helper function to convert date string to datetime-local format
const convertToDateTimeLocal = (dateString: string): string => {
  if (!dateString) return "";

  try {
    let cleanDateString = dateString.replace(" | ", " ");

    const dateTimeParts = cleanDateString.split(" ");
    const datePart = dateTimeParts[0];
    const timePart = dateTimeParts[1] || "00:00:00";

    const [day, month, year] = datePart.split("/");
    const [hours, minutes] = timePart.split(":");

    // Create ISO string and convert to datetime-local format
    const isoString = `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0"
    )}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;

    return isoString;
  } catch (error) {
    console.error("Error parsing date:", error);
    return "";
  }
};

const CreateDeliveryOrderDraft: React.FC<CreateDeliveryOrderDraftProps> = ({
  onBack,
  orderDataId,
  orderStage,
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<DeliveryOrderItem[]>([]);
  const [originalItems, setOriginalItems] = useState<DeliveryOrderItem[]>([]);
  const [selectedVendor, setSelectedVendor] =
    useState<null | OptionType<VendorListArray>>(null);
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<null | OptionType<Warehouse>>(null);
  const [orderDeadline, setOrderDeadline] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [quantityReceivedList, setQuantityReceivedList] = useState<string[]>(
    []
  );
  const [isAddingParts, setIsAddingParts] = useState(false);
  const { setLoading } = useLoading();
  const [showAddItemRow, setShowAddItemRow] = useState(false);
  const [availableItemsFromOrder, setAvailableItemsFromOrder] = useState<
    DeliveryOrderItem[]
  >([]);
  const [selectedPart, setSelectedPart] =
    useState<TransferOrderPartDetails | null>(null);
  const [parts, setParts] = useState<TransferOrderPartDetails[]>([]);
  const [hasMoreParts, setHasMoreParts] = useState(true);
  const [page, setPage] = useState(0);
  const partAutocompleteRef = useRef<any>(null);
  const [backOrderModalOpen, setBackOrderModalOpen] = useState(false);
  const [pendingBackOrderDecision, setPendingBackOrderDecision] = useState<
    boolean | null
  >(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [remarksList, setRemarksList] = useState<string[]>([]);

  // Invoice ID for completion
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [docketNumber, setDocketNumber] = useState<string>("");
  const [courierName, setCourierName] = useState<string>("");
  const [attachmentId, setAttachmentId] = useState<number | null>(null);
  const [attachmentFileName, setAttachmentFileName] = useState<string>("");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSuccessMessage = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const showErrorMessage = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setOrderDeadline(
      singleOrderDetails?.scheduledDate
        ? singleOrderDetails.scheduledDate.slice(0, 10)
        : ""
    );
    setIsEditMode(false);
  };

  const handleUpdateDeliveryOrder = async () => {
    if (!orderDataId || !singleOrderDetails || !orderDeadline) {
      showErrorMessage("Please select an order deadline");
      return;
    }

    setIsUpdating(true);
    const updateBody: CreateDeliveryOrderBody = {
      purchaseOrderId: singleOrderDetails.purchaseOrderId,
      status: singleOrderDetails.status.name,
      deliveryWarehouseId: deliveryWarehouseId ?? 0,
      deliveryOrderLineRequests: items.map((item, index) => ({
        partId: item.partId,
        quantityOrdered: Number(billedValues[index] || 0),
      })),
      parentId: singleOrderDetails.parentId ?? null,
      scheduledDate: `${orderDeadline}T10:00:00.000+00:00`,
      ...(singleOrderDetails.docketNumber && {
        docketNumber: singleOrderDetails.docketNumber,
      }),
      ...(singleOrderDetails.courierName && {
        courierName: singleOrderDetails.courierName,
      }),
      ...(singleOrderDetails.attachment && {
        attachment: Number(singleOrderDetails.attachment),
      }),
      remark: singleOrderDetails.remark ?? "",
    };

    try {
      const response = await DeliveryOrderApi.api.updateDeliveryOrder(
        updateBody,
        orderDataId
      );
      showSuccessMessage(
        response?.message || "Delivery order updated successfully"
      );
      await singleOrderData();
      setIsEditMode(false);
    } catch (error: any) {
      showErrorMessage(
        error?.response?.data?.message || "Error updating delivery order"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingAttachment(true);
      const uploadResponse = await UIDPartApi.api.uploadFile(file);
      const fileId = uploadResponse?.fileId ?? uploadResponse?.data?.fileId;

      if (!fileId) {
        throw new Error("Failed to get attachment file ID from server response");
      }

      setAttachmentId(fileId);
      setAttachmentFileName(file.name);
      showSuccessMessage("Attachment uploaded successfully");
    } catch (error: any) {
      console.error("Attachment upload failed", error);
      setAttachmentId(null);
      setAttachmentFileName("");
      showErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload attachment"
      );
    } finally {
      setIsUploadingAttachment(false);
      event.target.value = "";
    }
  };

  const handleQuantityReceivedChange = (
    index: number,
    value: number,
    maxQty: number
  ) => {
    if (value > maxQty) {
      return;
    }
    const updated = [...quantityReceivedList];
    updated[index] = value === 0 ? "" : value.toString();
    setQuantityReceivedList(updated);
  };

  const [sourceDocId, setSourceDocId] = useState<number | null>(null);

  const handleCloseModal = () => {
    setModalOpen(false);
    if (isSuccess) {
      window.location.reload();
    }
  };

  function fetchPaginatedPartsBySearchTerm(
    currentPageNumber: number,
    currentSearchQueryTerm: string
  ): Promise<{
    options: OptionType<TransferOrderPartDetails>[];
    hasMore: boolean;
  }> {
    return AllPartApi.api
      .getPartList(
        currentPageNumber,
        25,
        currentSearchQueryTerm,
        undefined,
        undefined,
        undefined
      )
      .then(({ data: response }) => ({
        options: response.data.map((part) => ({
          value: part.id.toString(),
          label: part.description,
          data: part,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  const [billedValues, setBilledValues] = useState<{
    [key: number]: string;
  }>({});

  const handleBilledChange = (index: number, val: string, quantity: number) => {
    // Remove any non-numeric characters
    const numericValue = val.replace(/[^0-9]/g, "");

    // Remove leading zeros
    const cleanedValue = numericValue.replace(/^0+/, "") || "0";

    // Only update if within valid range
    if (Number(cleanedValue) <= quantity) {
      setBilledValues((prev) => ({
        ...prev,
        [index]: cleanedValue === "0" ? "" : cleanedValue,
      }));
      handleExpectedQuantityChange(index, Number(cleanedValue), quantity);
    }
  };

  const [singleOrderDetails, setSingleOrderDetails] =
    useState<GetDeliveryOrderByIdObject>();

  const singleOrderData = useCallback(async () => {
    if (!orderDataId) return null;

    try {
      const response = await DeliveryOrderApi.api.getSingleDeliverOrderDetails(
        orderDataId
      );

      if (response?.data) {
        setSingleOrderDetails(response.data);
      }
    } catch (error) {
      showErrorMessage("Failed to fetch order details");
    }
  }, [orderDataId]);

  useEffect(() => {
    singleOrderData();
  }, [singleOrderData]);

  function fetchPaginatedDestinationLocationBySearchTerm(
    currentPageNumber: number,
    currentSearchQueryTerm: string
  ): Promise<{
    options: OptionType<Warehouse>[];
    hasMore: boolean;
  }> {
    return GetAllWarehouseListApi.api
      .getAllWarehouseList(currentPageNumber, 25, currentSearchQueryTerm)
      .then(({ data: response }) => ({
        options: response.data.map((destinationLocation) => ({
          value: destinationLocation.id.toString(),
          label: destinationLocation.name,
          data: destinationLocation,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  function fetchPaginatedVendorBySearchTerm(
    currentPageNumber: number,
    currentSearchQueryTerm: string
  ): Promise<{
    options: OptionType<VendorListArray>[];
    hasMore: boolean;
  }> {
    return GetAllVendorsListApi.api
      .getAllVendorsList(currentPageNumber, 25, currentSearchQueryTerm)
      .then(({ data: response }) => ({
        options: response.data.map((vendor) => ({
          value: vendor.id.toString(),
          label: vendor.name,
          data: vendor,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  const [destinationLocations, setDestinationLocations] = useState<Warehouse[]>(
    []
  );

  // const handleDeleteItem = (index: number) => {
  //   const deletedItem = items[index];

  //   setItems((prev) => prev.filter((_, i) => i !== index));
  //   setBilledValues((prev) => {
  //     const newValues = { ...prev };
  //     delete newValues[index];
  //     return newValues;
  //   });

  //   // Add deleted item back to available items if it's not a new item or if we're in CREATED stage
  //   if (!deletedItem.isNew || orderStage === "CREATED") {
  //     setAvailableItemsFromOrder((prev) => [...prev, deletedItem]);
  //   }

  //   // Show add item button when items are available
  //   if (availableItemsFromOrder.length > 0 || !deletedItem.isNew || orderStage === "CREATED") {
  //     setIsAddingParts(true);
  //   }
  //   setShowAddItemRow(false);
  // };

  const [deliveryWarehouseId, setDeliverWareHouseId] = useState<number | null>(
    null
  );

  const [quantityReturnedList, setQuantityReturnedList] = useState<string[]>(
    []
  );

  const handleQuantityReturnedChange = (
    index: number,
    value: number,
    maxQty: number
  ) => {
    if (value > maxQty) {
      return;
    }
    setQuantityReturnedList((prev) => {
      const updated = [...prev];
      updated[index] = value === 0 ? "" : value.toString();
      return updated;
    });
  };

  function handleExpectedQuantityChange(
    index: number,
    newQty: number,
    maxQty: number
  ) {
    if (newQty > maxQty) return;
    setItems((prevItems) => {
      const updatedItems = [...prevItems];
      const item = updatedItems[index];

      const price =
        typeof item.price === "string" ? parseFloat(item.price) : item.price;
      const totalWithTax = newQty * price;

      updatedItems[index] = {
        ...item,
        billed: newQty,
        total: parseFloat(totalWithTax.toFixed(2)),
      };

      return updatedItems;
    });
  }

  const handleDeleteItem = (index: number) => {
    const deletedItem = items[index];

    // Remove the item from the main items array
    setItems((prev) => prev.filter((_, i) => i !== index));

    // Remove the item from all related state arrays
    setBilledValues((prev) => {
      const newValues = { ...prev };
      delete newValues[index];
      // Reindex the remaining values
      const reindexed: Record<number, string> = {};
      Object.entries(newValues).forEach(([key, value]) => {
        const numKey = Number(key);
        if (numKey > index) {
          reindexed[numKey - 1] = value;
        } else if (numKey < index) {
          reindexed[numKey] = value;
        }
      });
      return reindexed;
    });

    setQuantityReceivedList((prev) => {
      const newList = [...prev];
      newList.splice(index, 1);
      return newList;
    });

    setQuantityReturnedList((prev) => {
      const newList = [...prev];
      newList.splice(index, 1);
      return newList;
    });

    setRemarksList((prev) => {
      const newList = [...prev];
      newList.splice(index, 1);
      return newList;
    });

    // Add deleted item back to available items if appropriate
    if (!deletedItem.isNew || orderStage === "CREATED") {
      setAvailableItemsFromOrder((prev) => [...prev, deletedItem]);
    }

    // Update add item button visibility
    if (
      availableItemsFromOrder.length > 0 ||
      !deletedItem.isNew ||
      orderStage === "CREATED"
    ) {
      setIsAddingParts(true);
    }
    setShowAddItemRow(false);
  };

  const initializeFormFromOrderDetails = useCallback(() => {
    if (singleOrderDetails) {
      setSourceDocId(singleOrderDetails.purchaseOrderId);
      setSelectedVendor({
        data: {} as unknown as VendorListArray,
        label: singleOrderDetails.vendorWarehouseDto?.vendorName,
        value: singleOrderDetails.vendorWarehouseDto?.vendorId,
      });
      setBilledValues(
        singleOrderDetails.deliveryOrderLines.reduce(
          (acc, item, index) => ({
            ...acc,
            [index]: item.quantityOrdered.toString(),
          }),
          {}
        )
      );
      setQuantityReceivedList(
        singleOrderDetails.deliveryOrderLines.map((item) =>
          (item.quantityPendingToReturn ?? 0).toString()
        )
      );
      setRemarksList(
        singleOrderDetails.deliveryOrderLines.map(
          (item) => item.returnReason || ""
        )
      );
      setOrderDeadline(
        singleOrderDetails.scheduledDate
          ? singleOrderDetails.scheduledDate.slice(0, 10)
          : ""
      );

      if (singleOrderDetails.deliveryWareHouseDetails?.deliveryWarehouseId) {
        setDeliverWareHouseId(
          singleOrderDetails.deliveryWareHouseDetails.deliveryWarehouseId
        );
      }

      setSelectedWarehouse({
        data: {} as unknown as Warehouse,
        label:
          singleOrderDetails.deliveryWareHouseDetails?.deliveryWarehouseName,
        value:
          singleOrderDetails.deliveryWareHouseDetails?.deliveryWarehouseId?.toString(),
      });

      if (singleOrderDetails.deliveryOrderLines) {
        const parsedItems = singleOrderDetails.deliveryOrderLines.map(
          (item) => ({
            id: item.partId ?? -1,
            externalId: 0,
            referenceCode: item.referenceCode ?? "N/A",
            type: "",
            description: item.partName ?? "",
            status: 0,
            costPrice: item.cost ?? 0,
            averageCostPrice: 0,
            salesPrice: 0,
            tax: Number(item.tax ?? 0),
            price: item.cost ?? 0,
            quantity: item.demand ?? 0,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
            partId: item.partId ?? 0,
            received: item.quantityPendingToReturn ?? 0,
            billed: item.quantityOrdered,
            unit: "Units",
            total: (item.quantityOrdered ?? 0) * (item.cost ?? 0),
            isNew: false,
          })
        );
        setItems(parsedItems);
        setOriginalItems(parsedItems);
        setAvailableItemsFromOrder([]);
        setIsAddingParts(false);
        setShowAddItemRow(false);
      }
    }
  }, [singleOrderDetails]);

  useEffect(() => {
    initializeFormFromOrderDetails();
  }, [initializeFormFromOrderDetails]);

  const handleAddItem = () => {
    setShowAddItemRow(true);
  };

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchParts = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      if (
        (orderStage === "DRAFT" || orderStage === "CREATED") &&
        availableItemsFromOrder.length > 0
      ) {
        // For DRAFT and CREATED stages, show available items from order
        const filteredItems = availableItemsFromOrder.filter((item) =>
          item.description.toLowerCase().includes(search.toLowerCase())
        );

        const formattedParts: TransferOrderPartDetails[] = filteredItems.map(
          (item) => ({
            id: item.partId,
            description: item.description,
            costPrice: item.price,
            averageCostPrice: 0,
            tax: item.tax || 0,
            partNumber: "",
            internalPartNumber: "",
            quantity: item.quantity,
            modelType: "",
            oemId: 0,
            oemName: "",
            modelTypeId: 0,
            createdDate: "",
            updatedDate: "",
            externalId: 0,
            referenceCode: "",
            type: "",
            status: 0,
            warehouseId: 0,
            warehouseName: "",
            vendorId: 0,
            vendorName: "",
            minQuantity: 0,
            maxQuantity: 0,
            salesPrice: 0,
            price: item.price,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
          })
        );

        setParts(formattedParts);
        setHasMoreParts(false);
      } else {
        // For other stages, fetch from API
        try {
          const response = await AllPartApi.api.getPartList(
            pageNumber,
            25,
            search,
            undefined,
            undefined,
            undefined
          );

          setParts(
            reset ? response.data.data : [...parts, ...response.data.data]
          );
          setHasMoreParts(pageNumber < response.data.totalPages - 1);
        } catch (error) {
          console.error("Error fetching parts", error);
          setParts([]);
          setHasMoreParts(false);
        }
      }
    },
    [availableItemsFromOrder, orderStage, parts]
  );

  const debouncedPartSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchParts(0, searchValue, true);
    }, 500),
    [fetchParts]
  );

  const handlePartSelect = (
    event: any,
    newValue: TransferOrderPartDetails | null
  ) => {
    if (newValue) {
      if (orderStage === "DRAFT" || orderStage === "CREATED") {
        const itemFromOrder = availableItemsFromOrder.find(
          (item) => item.partId === newValue.id
        );

        if (itemFromOrder) {
          const newItemIndex = items.length; // Get the index where new item will be added

          setItems([...items, itemFromOrder]);

          // Initialize billedValues for the new item
          setBilledValues((prev) => ({
            ...prev,
            [newItemIndex]:
              itemFromOrder.billed > 0 ? itemFromOrder.billed.toString() : "0",
          }));

          // Initialize other arrays if needed
          setQuantityReceivedList((prev) => {
            const newList = [...prev];
            newList[newItemIndex] = itemFromOrder.received.toString();
            return newList;
          });

          setQuantityReturnedList((prev) => {
            const newList = [...prev];
            newList[newItemIndex] = "0";
            return newList;
          });

          setRemarksList((prev) => {
            const newList = [...prev];
            newList[newItemIndex] = "";
            return newList;
          });

          setAvailableItemsFromOrder((prev) =>
            prev.filter((item) => item.partId !== newValue.id)
          );
          setSelectedPart(null);
          setShowAddItemRow(false);

          if (availableItemsFromOrder.length <= 1) {
            setIsAddingParts(false);
          }
        }
      } else {
        // For other stages, add new item
        const newItem: DeliveryOrderItem = {
          id: newValue.id,
          externalId: newValue.externalId || 0,
          referenceCode: newValue.referenceCode || "",
          type: newValue.type || "",
          description: newValue.description || "",
          status: newValue.status || 0,
          costPrice: newValue.costPrice || 0,
          averageCostPrice: newValue.averageCostPrice || 0,
          salesPrice: newValue.salesPrice || 0,
          tax: newValue.tax || 0,
          price: newValue.costPrice || 0,
          quantity: 0,
          penaltyType: "",
          deleted: false,
          updatedBy: 0,
          partId: newValue.id,
          received: 0,
          billed: 0,
          unit: "Units",
          total: 0,
          isNew: true,
        };

        const newItemIndex = items.length;
        setItems([...items, newItem]);

        // Initialize billedValues for new item
        setBilledValues((prev) => ({
          ...prev,
          [newItemIndex]: "0",
        }));

        setSelectedPart(null);
        setShowAddItemRow(false);
      }
    }
  };

  useEffect(() => {
    if (showAddItemRow) {
      fetchParts(0, "", true);
    }
  }, [fetchParts, showAddItemRow, availableItemsFromOrder]);

  const createDeliveryOrder = useCallback(
    async (status: string) => {
      setLoading(true);
      const createDeliveryOrderBody: CreateDeliveryOrderBody = {
        purchaseOrderId: singleOrderDetails?.purchaseOrderId ?? 0,
        status: status,
        deliveryWarehouseId: deliveryWarehouseId ?? 0,
        deliveryOrderLineRequests: items
          .filter((item) => !item.isNew || (item.isNew && item.billed > 0))
          .map((item, index) => ({
            partId: item.id,
            quantityOrdered: Number(billedValues[index] || 0),
          })),
        parentId: null,
        scheduledDate: orderDeadline
          ? `${orderDeadline}T10:00:00.000+00:00`
          : new Date().toISOString(),
        ...(docketNumber.trim() && { docketNumber: docketNumber.trim() }),
        ...(courierName.trim() && { courierName: courierName.trim() }),
        ...(attachmentId && { attachment: attachmentId }),
      };
      try {
        const response = await DeliveryOrderApi.api.createDeliveryOrderDraft(
          createDeliveryOrderBody,
          singleOrderDetails?.id ?? 0
        );
        showSuccessMessage(
          response?.message || "Delivery order created successfully"
        );
        // Use onBack callback instead of navigate
        setTimeout(() => {
          onBack();
        }, 1000);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || "Error creating delivery order";
        showErrorMessage(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      deliveryWarehouseId,
      items,
      sourceDocId,
      orderDeadline,
      billedValues,
      navigate,
      singleOrderDetails,
      onBack,
      docketNumber,
      courierName,
      attachmentId,
      isUploadingAttachment,
    ]
  );

  useEffect(() => {
    if (selectedWarehouse) {
      // Check if we have a numeric value in selectedWarehouse.value
      const warehouseId = selectedWarehouse.value
        ? Number(selectedWarehouse.value)
        : null;
      if (warehouseId && !isNaN(warehouseId)) {
        setDeliverWareHouseId(warehouseId);
      }
    }
  }, [selectedWarehouse]);

  const createReturnToVendor = useCallback(
    async (status: string) => {
      setLoading(true);
      const createReturnToVendorBody: ReturnOrderRequestBody = {
        orderId: singleOrderDetails?.id,
        orderStatus: status,
        orderReturnList: items.map((item, index) => ({
          partId: item.partId,
          quantity: Number(quantityReturnedList[index] || 0),
          returnReason: remarksList[index] || "",
        })),
      };

      try {
        const response = await DeliveryReturnApi.api.createDeliveryReturn(
          createReturnToVendorBody
        );
        showSuccessMessage("Return order created successfully");
        setTimeout(() => {
          onBack();
        }, 2000);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || "Error creating return order";
        showErrorMessage(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [items, quantityReturnedList, remarksList, singleOrderDetails, onBack]
  );

  const createDeliveryOrderCompleted = useCallback(
    async (status: string, createBackOrder: boolean = false) => {
      setLoading(true);
      const createDeliveryOrderRecievedBody: CreateDeliveryOrderRecievedBody = {
        purchaseOrderId: sourceDocId ?? 0,
        status: status,
        deliveryWarehouseId: deliveryWarehouseId ?? 0,
        deliveryOrderLineRequests: items.map((item, index) => ({
          partId: item.id,
          quantityReceived: Number(quantityReceivedList[index] || 0),
        })),
        parentId: null,
        scheduledDate: orderDeadline || new Date()?.toISOString(),
        createBackOrder: createBackOrder,
        invoiceId: invoiceId.trim(),
      };
      try {
        const response = await DeliveryOrderApi.api.createDeliveryOrderRecieved(
          createDeliveryOrderRecievedBody,
          singleOrderDetails?.id ?? 0
        );
        showSuccessMessage("Delivery order completed successfully");
        // Use onBack callback or force reload
        setTimeout(() => {
          // First try onBack
          onBack();
          // If we're still on the same page after a short delay, force reload
          setTimeout(() => {
            if (window.location.pathname.includes("delivery-order")) {
              window.location.href = "/delivery-order";
            }
          }, 100);
        }, 1000);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || "Error completing delivery order";
        showErrorMessage(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      deliveryWarehouseId,
      items,
      sourceDocId,
      orderDeadline,
      quantityReceivedList,
      navigate,
      singleOrderDetails,
      onBack,
      invoiceId,
    ]
  );

  const handleCompleteOrder = useCallback(() => {
    // Validate that invoice ID is provided
    if (!invoiceId || invoiceId.trim() === "") {
      showErrorMessage("Please enter an Invoice ID before completing the order.");
      return;
    }

    // Check if any item has received less quantity than ordered
    const hasLessQuantityReceived = singleOrderDetails?.deliveryOrderLines.some(
      (item, index) => {
        const quantityOrdered = item.quantityOrdered;
        const quantityReceived = Number(quantityReceivedList[index] || 0);
        return quantityReceived < quantityOrdered;
      }
    );

    if (hasLessQuantityReceived) {
      // Open modal for back order confirmation
      setBackOrderModalOpen(true);
    } else {
      // Complete order without back order
      createDeliveryOrderCompleted("COMPLETED", false);
    }
  }, [singleOrderDetails, quantityReceivedList, createDeliveryOrderCompleted, invoiceId, showErrorMessage]);

  const handleBackOrderDecision = (createBackOrder: boolean) => {
    setBackOrderModalOpen(false);
    createDeliveryOrderCompleted("COMPLETED", createBackOrder);
  };

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", path: "/delivery-order", clickHandler: onBack },
      { label: "Delivery", path: "/delivery-order", clickHandler: onBack },
      { label: "Delivery Details", path: "", clickHandler: null },
    ],
    [onBack]
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              Delivery Details
            </Typography>

            {orderDataId && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 500,
                  }}>
                  History
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F8F9FA",
              padding: "12px 16px",
              borderRadius: "8px",
            }}>
            <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {crumb.clickHandler ? (
                    <button
                      onClick={crumb.clickHandler}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="text-sm font-medium text-gray-700">
                      {crumb.label}
                    </span>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <span className="text-sm text-gray-400">/</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Box>
        </Box>

        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Vendor Name
                  </label>
                  <AppAutoCompleteWithSearchAPI
                    initialPageNumber={0}
                    value={selectedVendor}
                    onChange={function (
                      _: unknown,
                      value: OptionType<VendorListArray> | null
                    ): void {
                      setSelectedVendor(value);
                    }}
                    getData={(data: {
                      currentPageNumber: number;
                      currentSearchQueryTerm: string;
                    }) => {
                      return fetchPaginatedVendorBySearchTerm(
                        data.currentPageNumber,
                        data.currentSearchQueryTerm
                      );
                    }}
                    disabled={
                      orderStage === "DRAFT" ||
                      orderStage === "CREATED" ||
                      orderStage === "RETURNED"
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    PO Id
                  </label>
                  <input
                    disabled
                    type="number"
                    value={sourceDocId ?? ""}
                    onChange={(e) => setSourceDocId(Number(e.target.value))}
                    className="w-full border-b focus:outline-none p-1 text-sm"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Expected Delivery Date
                  </label>
                  <TextField
                    fullWidth
                    type="date"
                    value={orderDeadline}
                    onChange={(e) => setOrderDeadline(e.target.value)}
                    disabled={orderDataId ? !isEditMode : false}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Destination Location
                  </label>
                  <AppAutoCompleteWithSearchAPI
                    initialPageNumber={0}
                    value={selectedWarehouse}
                    onChange={function (
                      event: any,
                      value: OptionType<Warehouse> | null
                    ): void {
                      setSelectedWarehouse(value);
                      if (value && value.data) {
                        setDeliverWareHouseId(value.data.id);
                      } else if (value && value.value) {
                        setDeliverWareHouseId(Number(value.value));
                      } else {
                        setDeliverWareHouseId(null);
                      }
                    }}
                    getData={function (data: {
                      currentPageNumber: number;
                      currentSearchQueryTerm: string;
                    }): Promise<{
                      options: OptionType<Warehouse>[];
                      hasMore: boolean;
                    }> {
                      return fetchPaginatedDestinationLocationBySearchTerm(
                        data.currentPageNumber,
                        data.currentSearchQueryTerm
                      );
                    }}
                    disabled={
                      orderStage === "DRAFT" ||
                      orderStage === "CREATED" ||
                      orderStage === "RETURNED"
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textAlign: "start" }}>
                  Source DO
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    my: "1rem",
                    textAlign: "start",
                  }}>
                  {singleOrderDetails?.parentId || "-"}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textAlign: "start" }}>
                  Docket Number
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    my: "1rem",
                    textAlign: "start",
                  }}>
                  {singleOrderDetails?.docketNumber || "-"}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textAlign: "start" }}>
                  Courier Name
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #ccc",
                    my: "1rem",
                    textAlign: "start",
                  }}>
                  {singleOrderDetails?.courierName || "-"}
                </Box>
              </Grid>
              {singleOrderDetails?.attachment && (
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      my: "1rem",
                      textAlign: "start",
                    }}>
                    <Button
                      component="a"
                      variant="contained"
                      href={`${MEDIA_BASE_URL}/${singleOrderDetails.attachment}`}
                      download
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<DownloadIcon />}
                      sx={{
                        minWidth: 190,
                        height: 42,
                        justifyContent: "center",
                        px: 2,
                        textTransform: "none",
                        borderRadius: "999px",
                        background: "linear-gradient(135deg, #16a34a, #0f766e)",
                        fontWeight: 600,
                        boxShadow: "0 6px 14px rgba(22, 163, 74, 0.22)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #15803d, #115e59)",
                          boxShadow: "0 8px 18px rgba(22, 163, 74, 0.3)",
                        },
                      }}>
                      Download Attachment
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>

            <TableContainer
              sx={{
                overflowX: "auto",
                maxWidth: "100%",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "4px",
                },
              }}>
              <Table
                sx={{ tableLayout: "fixed", width: "100%", minWidth: 1600 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#ffffff" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "40px",
                        width: "40px",
                      }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "200px",
                        width: "200px",
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "150px",
                        width: "150px",
                      }}>
                      Reference Code
                    </TableCell>
                    {orderStage === "DRAFT" && (
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          minWidth: "80px",
                          width: "80px",
                        }}>
                        Demand
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "120px",
                        width: "120px",
                      }}>
                      Quantity Ordered
                    </TableCell>

                    {(orderStage === "CREATED" ||
                      orderStage === "RETURNED") && (
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          minWidth: "120px",
                          width: "120px",
                        }}>
                        Quantity Received
                      </TableCell>
                    )}

                    {orderStage === "RETURNED" && (
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          minWidth: "120px",
                          width: "120px",
                        }}>
                        Quantity Returned
                      </TableCell>
                    )}

                    {orderStage === "RETURNED" && (
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          minWidth: "150px",
                          width: "150px",
                        }}>
                        Remarks
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "100px",
                        width: "100px",
                      }}>
                      Unit Of Measure
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "80px",
                        width: "80px",
                      }}>
                      Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "80px",
                        width: "80px",
                      }}>
                      Tax (%)
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "80px",
                        width: "80px",
                      }}>
                      Cost
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "80px",
                        width: "80px",
                      }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#ffffff" },
                        "&:hover": { backgroundColor: "#e3f2fd" },
                      }}>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.referenceCode}
                      </TableCell>
                      {orderStage === "DRAFT" && (
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {item.quantity}
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField
                          variant="standard"
                          fullWidth
                          type="text"
                          placeholder="Enter Qty"
                          value={billedValues[index] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleBilledChange(
                              index,
                              value,
                              Number(item.quantityPendingToReturn)
                            );
                          }}
                          onBlur={(e) => {
                            // If empty, set to 0
                            if (e.target.value === "") {
                              setBilledValues((prev) => ({
                                ...prev,
                                [index]: "0",
                              }));
                              handleExpectedQuantityChange(
                                index,
                                0,
                                Number(item.quantityPendingToReturn)
                              );
                            }
                          }}
                          disabled={
                            orderStage === "CREATED" ||
                            orderStage === "RETURNED" ||
                            (orderStage === "CREATED" && isEditMode)
                          }
                          inputProps={{
                            min: 0,
                            // max: item.quantity,
                            // maxLength: item.quantity.toString().length + 2,
                            style: {
                              fontSize: "0.875rem",
                              MozAppearance: "textfield",
                              appearance: "textfield",
                            },
                          }}
                          InputProps={{
                            sx: {
                              fontSize: "0.875rem",
                              px: 1,
                              py: 0.5,
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                                "&::-webkit-outer-spin-button": {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                                "&::-webkit-inner-spin-button": {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                              },
                            },
                          }}
                          sx={{
                            minWidth: "100px",
                            "& .MuiInput-root": {
                              "&:before, &:after": {
                                borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                              },
                            },
                          }}
                        />
                      </TableCell>
                      {(orderStage === "CREATED" ||
                        orderStage === "RETURNED") && (
                        <TableCell>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="text"
                            placeholder="Enter Qty"
                            disabled={
                              orderStage === "RETURNED" ||
                              (orderStage === "CREATED" && isEditMode)
                            }
                            value={quantityReceivedList[index] ?? ""}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              const cleanedValue =
                                value.replace(/^0+/, "") || "0";

                              if (
                                Number(cleanedValue) <=
                                Number(
                                  singleOrderDetails?.deliveryOrderLines[index]
                                    .quantityOrdered
                                )
                              ) {
                                handleQuantityReceivedChange(
                                  index,
                                  Number(cleanedValue),
                                  Number(
                                    singleOrderDetails?.deliveryOrderLines[
                                      index
                                    ].quantityOrdered
                                  )
                                );
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                handleQuantityReceivedChange(
                                  index,
                                  0,
                                  Number(
                                    singleOrderDetails?.deliveryOrderLines[
                                      index
                                    ].quantityOrdered
                                  )
                                );
                              }
                            }}
                            inputProps={{
                              maxLength:
                                (
                                  singleOrderDetails?.deliveryOrderLines[index]
                                    .quantityOrdered || 0
                                ).toString().length + 2,
                              style: {
                                fontSize: "0.875rem",
                                MozAppearance: "textfield",
                                appearance: "textfield",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontSize: "0.875rem",
                                px: 1,
                                py: 0.5,
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                  "&::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "&::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                },
                              },
                            }}
                            sx={{
                              minWidth: "100px",
                              "& .MuiInput-root": {
                                "&:before, &:after": {
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                                },
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      {orderStage === "RETURNED" && (
                        <TableCell>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="text"
                            placeholder="Enter Qty"
                            disabled={orderStage !== "RETURNED"}
                            value={quantityReturnedList[index] ?? ""}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              const cleanedValue =
                                value.replace(/^0+/, "") || "0";

                              if (
                                Number(cleanedValue) <=
                                Number(
                                  singleOrderDetails?.deliveryOrderLines[index]
                                    .quantityPendingToReturn
                                )
                              ) {
                                handleQuantityReturnedChange(
                                  index,
                                  Number(cleanedValue),
                                  Number(
                                    singleOrderDetails?.deliveryOrderLines[
                                      index
                                    ].quantityPendingToReturn
                                  )
                                );
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                handleQuantityReturnedChange(
                                  index,
                                  0,
                                  Number(
                                    singleOrderDetails?.deliveryOrderLines[
                                      index
                                    ].quantityPendingToReturn
                                  )
                                );
                              }
                            }}
                            inputProps={{
                              maxLength:
                                (
                                  singleOrderDetails?.deliveryOrderLines[index]
                                    .quantityPendingToReturn || 0
                                ).toString().length + 2,
                              style: {
                                fontSize: "0.875rem",
                                MozAppearance: "textfield",
                                appearance: "textfield",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontSize: "0.875rem",
                                px: 1,
                                py: 0.5,
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                  "&::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                  "&::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                },
                              },
                            }}
                            sx={{
                              minWidth: "100px",
                              "& .MuiInput-root": {
                                "&:before, &:after": {
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                                },
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      {orderStage === "RETURNED" && (
                        <TableCell>
                          <TextField
                            variant="standard"
                            fullWidth
                            placeholder="Enter remarks"
                            value={remarksList[index] ?? ""}
                            onChange={(e) => {
                              const updatedRemarks = [...remarksList];
                              updatedRemarks[index] = e.target.value;
                              setRemarksList(updatedRemarks);
                            }}
                            inputProps={{
                              style: {
                                fontSize: "0.875rem",
                              },
                            }}
                            InputProps={{
                              sx: {
                                fontSize: "0.875rem",
                                px: 1,
                                py: 0.5,
                              },
                            }}
                            sx={{
                              minWidth: "100px",
                              "& .MuiInput-root": {
                                "&:before, &:after": {
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                                },
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.unit}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        ₹
                        {typeof item.price === "number"
                          ? item.price.toFixed(2)
                          : item.price}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.tax ? `${item.tax}%` : "0%"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        ₹{item.total.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteItem(index)}
                          sx={{ color: "#FF0000" }}
                          size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {showAddItemRow && (
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell
                        colSpan={
                          orderStage === "RETURNED"
                            ? 12 // Updated count
                            : orderStage === "CREATED"
                            ? 11 // Updated count
                            : orderStage === "DRAFT"
                            ? 10 // Updated count
                            : 9
                        }
                        sx={{ textAlign: "center", py: 2 }}>
                        <Autocomplete
                          ref={partAutocompleteRef}
                          options={parts}
                          getOptionLabel={(option) => option.description}
                          value={selectedPart}
                          onChange={handlePartSelect}
                          onInputChange={(event, newInputValue) => {
                            debouncedPartSearch(newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder="Select part to add"
                              sx={{ maxWidth: 400, mx: "auto" }}
                            />
                          )}
                          sx={{ maxWidth: 400, mx: "auto" }}
                          key={availableItemsFromOrder.length}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {(orderStage === "DRAFT" || orderStage === "CREATED") &&
              isAddingParts &&
              !showAddItemRow &&
              availableItemsFromOrder.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    sx={{
                      textTransform: "none",
                      borderRadius: "20px",
                      px: 3,
                    }}>
                    Add Item
                  </Button>
                </Box>
              )}

            {(orderStage === "DRAFT" || orderStage === "CREATED") &&
              isAddingParts &&
              availableItemsFromOrder.length === 0 && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No more items available to add
                  </Typography>
                </Box>
              )}

            {orderStage === "DRAFT" && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Docket Number"
                      value={docketNumber}
                      onChange={(e) => setDocketNumber(e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Courier Name"
                      value={courierName}
                      onChange={(e) => setCourierName(e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      hidden
                      onChange={handleAttachmentChange}
                    />
                    <Box>
                      <Button
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => attachmentInputRef.current?.click()}
                        disabled={isUploadingAttachment}
                        sx={{
                          width: 150,
                          height: 42,
                          justifyContent: "center",
                          px: 2,
                          borderRadius: "999px",
                          background: "linear-gradient(135deg, #16a34a, #0f766e)",
                          boxShadow: "0 6px 14px rgba(22, 163, 74, 0.22)",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": {
                            background: "linear-gradient(135deg, #15803d, #115e59)",
                            boxShadow: "0 8px 18px rgba(22, 163, 74, 0.3)",
                          },
                        }}>
                        {isUploadingAttachment ? "Uploading..." : "Upload LR"}
                      </Button>
                      {attachmentFileName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}>
                          {attachmentFileName}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {orderStage === "DRAFT" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}>
                <Button
                  variant="contained"
                  color="error"
                  sx={{
                    marginRight: 2,
                    padding: "8px 20px",
                    borderRadius: "32px",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}
                  onClick={onBack}>
                  Close Order
                </Button>
                <Button
                  onClick={async () => {
                    await createDeliveryOrder("DRAFT");
                  }}
                  variant="contained"
                  color="primary"
                  sx={{
                    padding: "8px 20px",
                    borderRadius: "32px",
                    backgroundColor: "#1E1E1E",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    "&:hover": { backgroundColor: "#000000" },
                  }}>
                  Draft
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    await createDeliveryOrder("CREATED");
                  }}
                  sx={{
                    padding: "8px 20px",
                    marginLeft: 2,
                    borderRadius: "32px",
                    backgroundColor: "#1E1E1E",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    "&:hover": { backgroundColor: "#000000" },
                  }}>
                  Create Delivery Order
                </Button>
              </Box>
            )}

            {orderStage === "CREATED" && (
              <>
                {/* Invoice ID Input Section */}
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Invoice ID *"
                        type="text"
                        variant="outlined"
                        size="small"
                        value={invoiceId}
                        onChange={(e) => setInvoiceId(e.target.value)}
                        disabled={isEditMode}
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}>
                  <Button
                    onClick={handleCompleteOrder}
                    variant="contained"
                    color="primary"
                    sx={{
                      padding: "8px 20px",
                      borderRadius: "32px",
                      backgroundColor: "#1E1E1E",
                      textTransform: "none",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      "&:hover": { backgroundColor: "#000000" },
                    }}>
                    Complete Order
                  </Button>
                    {!isEditMode ? (
                      <Button
                        variant="contained"
                        onClick={handleEditClick}
                        sx={{
                          padding: "8px 20px",
                          borderRadius: "32px",
                          textTransform: "none",
                          fontWeight: "500",
                          fontSize: "0.875rem",
                        }}>
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleUpdateDeliveryOrder}
                          disabled={isUpdating}
                          sx={{
                            padding: "8px 20px",
                            borderRadius: "32px",
                            textTransform: "none",
                            fontWeight: "500",
                            fontSize: "0.875rem",
                          }}>
                          {isUpdating ? "Updating..." : "Update"}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                          sx={{
                            padding: "8px 20px",
                            borderRadius: "32px",
                            textTransform: "none",
                            fontWeight: "500",
                            fontSize: "0.875rem",
                          }}>
                          Cancel Edit
                        </Button>
                      </>
                    )}
                </Box>
              </>
            )}

            {orderStage === "RETURNED" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}>
                <Button
                  variant="contained"
                  color="error"
                  sx={{
                    marginRight: 2,
                    padding: "8px 20px",
                    borderRadius: "32px",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}
                  onClick={onBack}>
                  Close Order
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  sx={{
                    marginRight: 2,
                    padding: "12px 20px",
                    borderRadius: "32px",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}
                  onClick={async () => {
                    await createReturnToVendor("DRAFT");
                  }}>
                  Draft
                </Button>
                <Button
                  onClick={async () => {
                    await createReturnToVendor("COMPLETED");
                  }}
                  variant="contained"
                  color="primary"
                  sx={{
                    padding: "8px 20px",
                    borderRadius: "32px",
                    backgroundColor: "#1E1E1E",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    "&:hover": { backgroundColor: "#000000" },
                  }}>
                  Return To Vendor
                </Button>
              </Box>
            )}
          </Box>

          <Modal
            open={modalOpen}
            onClose={handleCloseModal}
            aria-labelledby="modal-title"
            aria-describedby="modal-description">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 300,
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
                textAlign: "center",
                borderRadius: "8px",
                backgroundColor: isSuccess ? "#d4edda" : "#f8d7da",
                color: isSuccess ? "#155724" : "#721c24",
              }}>
              <Typography
                id="modal-title"
                variant="h6"
                component="h2"
                sx={{ fontSize: "1rem", mb: 2 }}>
                {modalMessage}
              </Typography>
              <Button onClick={handleCloseModal} sx={{ fontSize: "0.875rem" }}>
                Close
              </Button>
            </Box>
          </Modal>

          {/* Back Order Confirmation Modal */}
          <Modal
            open={backOrderModalOpen}
            onClose={() => setBackOrderModalOpen(false)}
            aria-labelledby="backorder-modal-title"
            aria-describedby="backorder-modal-description">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 420,
                bgcolor: "background.paper",
                borderRadius: "8px",
                boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
                p: 0,
                overflow: "hidden",
              }}>
              {/* Modal Header */}
              <Box
                sx={{
                  background: "linear-gradient(to bottom, #1a1a1a, #2d2d2d)",
                  p: 3,
                  textAlign: "center",
                }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      color: "white",
                    }}>
                    ⚠️
                  </Typography>
                </Box>
                <Typography
                  id="backorder-modal-title"
                  variant="h6"
                  component="h2"
                  sx={{
                    color: "white",
                    fontWeight: "500",
                    fontSize: "1.125rem",
                    letterSpacing: "-0.02em",
                  }}>
                  Items Shortage Detected
                </Typography>
              </Box>

              {/* Modal Body */}
              <Box sx={{ p: 4 }}>
                <Typography
                  id="backorder-modal-description"
                  sx={{
                    fontSize: "0.875rem",
                    color: "#4a4a4a",
                    textAlign: "center",
                    mb: 3.5,
                    lineHeight: 1.5,
                  }}>
                  You have received less items than expected. Do you want to
                  create a back order to the same vendor?
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}>
                  <Button
                    onClick={() => handleBackOrderDecision(false)}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      padding: "10px 20px",
                      borderRadius: "6px",
                      borderColor: "#d1d1d1",
                      color: "#2d2d2d",
                      textTransform: "none",
                      fontWeight: "400",
                      fontSize: "0.875rem",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#888",
                        backgroundColor: "#fafafa",
                      },
                    }}>
                    No, Complete As Is
                  </Button>
                  <Button
                    onClick={() => handleBackOrderDecision(true)}
                    variant="contained"
                    sx={{
                      flex: 1,
                      padding: "10px 20px",
                      borderRadius: "6px",
                      background: "#1a1a1a",
                      color: "white",
                      textTransform: "none",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: "#2d2d2d",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                      },
                    }}>
                    Yes, Create Back Order
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>
        </Paper>

        {orderDataId && (
          <DeliveryOrderHistory
            deliveryOrderId={orderDataId}
            variant="dialog"
            open={historyDialogOpen}
            onClose={() => setHistoryDialogOpen(false)}
          />
        )}

        {/* Snackbar for showing API messages */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default CreateDeliveryOrderDraft;
